import React, { useState, useEffect } from "react";
import Lottie from "lottie-react";
import successAnimation from "./assets/payment_success.json";
import nextAnimation from "./assets/order_confirm.json";

import failureAnimation from "./assets/payment_fail.json";
import pendingAnimation from "./assets/payment_pending.json";
import { ref, get } from "firebase/database";
import { database as realtimeDb } from "../FirebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

export default function PaymentStatus() {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [currentAnimation, setCurrentAnimation] = useState(successAnimation);

  const [isFetched, setIsFetched] = useState(false);


  const processPaymentCallback = async (orderId, mId) => {
    try {
      const response = await axios.post(
        `https://us-central1-tghc-web.cloudfunctions.net/callbackPayment?orderId=${orderId}&merchantTransactionId=${mId}`,
      );
      console.log("Payment callback response:", response.data);
    } catch (error) {
      console.error("Error processing payment callback:", error);
    }
  };


  useEffect(() => {
    if (isFetched) {
      const timer = setTimeout(() => {
        setCurrentAnimation(nextAnimation); // Switch to the next animation after 3 seconds
      }, 3000); // 3000ms = 3 seconds

      // Cleanup the timeout on unmount
      return () => clearTimeout(timer);
    }
  }, [isFetched]);

  // Extract orderId from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get("id");
    const mId = queryParams.get("mId");

    if (orderId) {
      fetchOrderDetails(orderId);
      if (mId) {
        processPaymentCallback(orderId, mId);
      }

    } else {
      setError("Invalid Order ID. Please check your URL.");
    }
  }, [location.search]);

  // **🔹 Fetch Order Details from Realtime Database**
  const fetchOrderDetails = async (orderId) => {
    setLoading(true);
    setError("");
    setOrderData(null);

    try {
      const orderRef = ref(realtimeDb, `orders/${orderId}`);
      const orderSnap = await get(orderRef);

      if (orderSnap.exists()) {
        setOrderData(orderSnap.val()); // Get order data from RTDB
      } else {
        setError("Order not found. Please check your Order ID.");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("Something went wrong. Please try again.");
    }

    setIsFetched(true);
    setLoading(false);
  };


  useEffect(() => {
    if (orderData?.paymentStatus === "Completed") {
      window.gtag && window.gtag("event", "conversion", {
        send_to: 'AW-456297411/VVmBCJWO5ogCEMOXytkB',
        value: orderData.totalAmount || 0,
        currency: "INR",
      });
    }
  }, [orderData]);

  // **🔹 Render Payment Status UI**
  const renderPaymentStatus = () => {
    if (!orderData) return null;

    
    switch (orderData.paymentStatus) {
      case "Completed":
        
        return (
          <div className="text-center">
            <Lottie animationData={currentAnimation} className="w-64 mx-auto" />
            <h2 className="text-[#f47d02] font-extrabold text-2xl mt-4">Payment Successful! 🍯</h2>
            <p className="text-gray-600 mt-2">Your golden honey is on its way! 🐝</p>
            <p className="text-gray-600">
              <span className="font-semibold">Tracking ID:</span> {orderData.trackingId}
            </p>
          </div>
        );

      case "Failed":
        return (
          <div className="text-center">
            <Lottie animationData={failureAnimation} className="w-64 mx-auto" />
            <h2 className="text-red-600 font-extrabold text-2xl mt-4">Payment Failed! ❌</h2>
            <p className="text-gray-600 mt-2">Oops! The bees couldn’t complete your order. 🐝</p>
            <p className="text-gray-600">Try again and bring sweetness to your day! 🍯</p>
          </div>
        );

      case "Pending":
        return (
          <div className="text-center">
            <Lottie animationData={pendingAnimation} className="w-64 mx-auto" />
            <h2 className="text-yellow-600 font-extrabold text-2xl mt-4">Payment Pending! ⏳</h2>
            <p className="text-gray-600 mt-2">Your honey jar is being prepared! 🍯</p>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <p className="text-gray-600">No payment information available for this order.</p>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#fef3e6] p-6">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg text-center">
        <h2 className="text-2xl font-bold text-[#f47d02]">Payment Status</h2>
        <p className="text-gray-600 mt-1">Track your honey order here! 🍯🐝</p>

        {/* Loading State */}
        {loading && <p className="text-gray-500 mt-3">Fetching order details...</p>}

        {/* Error Message */}
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        {/* Payment Status Display */}
        {renderPaymentStatus()}

        {/* Back to Home Button */}
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-[#f47d02] text-white px-4 py-2 rounded-md hover:bg-orange-600 transition"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}
